<template>
  <span
    class="glossary-term"
    @mouseenter="openTooltip"
    @mouseleave="closeTooltip"
  >
    <button
      ref="trigger"
      type="button"
      class="glossary-term__trigger"
      :aria-expanded="open ? 'true' : 'false'"
      :aria-describedby="open ? tooltipId : null"
      @focus="openTooltip"
      @blur="closeTooltip"
      @keydown.esc.prevent="closeTooltip"
    >
      {{ entry.term }}
    </button>

    <Teleport to="body">
      <transition name="glossary-fade">
        <div
          v-if="open"
          :id="tooltipId"
          ref="tooltip"
          class="glossary-term__tooltip-layer"
          :style="tooltipStyle"
          role="tooltip"
        >
          <strong>{{ entry.term }}</strong>
          <span>{{ entry.shortDefinition }}</span>
          <span v-if="entry.longDefinition">{{ entry.longDefinition }}</span>
          <span v-if="entry.example" class="glossary-term__example">
            {{ entry.example }}
          </span>
        </div>
      </transition>
    </Teleport>
  </span>
</template>

<script>
export default {
  name: 'GlossaryTerm',
  props: {
    entry: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      open: false,
      tooltipId: `glossary-tooltip-${Math.random().toString(36).slice(2, 10)}`,
      tooltipStyle: {
        top: '0px',
        left: '0px',
        maxWidth: '320px',
        visibility: 'hidden'
      },
      rafId: null
    };
  },
  watch: {
    open(isOpen) {
      if (isOpen) {
        this.tooltipStyle = {
          top: '0px',
          left: '0px',
          maxWidth: '320px',
          visibility: 'hidden'
        };

        this.$nextTick(() => {
          this.updateTooltipPosition();
        });

        window.addEventListener('resize', this.updateTooltipPosition);
        window.addEventListener('scroll', this.handleGlobalScroll, true);
      } else {
        window.removeEventListener('resize', this.updateTooltipPosition);
        window.removeEventListener('scroll', this.handleGlobalScroll, true);
      }
    }
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.updateTooltipPosition);
    window.removeEventListener('scroll', this.handleGlobalScroll, true);

    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  },
  methods: {
    openTooltip() {
      this.open = true;
    },

    closeTooltip() {
      this.open = false;
    },

    handleGlobalScroll() {
      if (!this.open) {
        return;
      }

      this.scheduleTooltipPosition();
    },

    scheduleTooltipPosition() {
      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
      }

      this.rafId = requestAnimationFrame(() => {
        this.updateTooltipPosition();
      });
    },

    updateTooltipPosition() {
      const triggerEl = this.$refs.trigger;
      const tooltipEl = this.$refs.tooltip;

      if (!triggerEl || !tooltipEl) {
        return;
      }

      const triggerRect = triggerEl.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      const sidePadding = 12;
      const gap = 10;
      const topSafeArea = 76;
      const tooltipMaxWidth = Math.min(320, viewportWidth - sidePadding * 2);

      tooltipEl.style.maxWidth = `${tooltipMaxWidth}px`;

      const tooltipWidth = Math.min(tooltipEl.offsetWidth || tooltipMaxWidth, tooltipMaxWidth);
      const tooltipHeight = tooltipEl.offsetHeight || 0;

      const roomAbove = triggerRect.top - topSafeArea;
      const roomBelow = viewportHeight - triggerRect.bottom - sidePadding;

      const placeAbove = roomAbove >= tooltipHeight + gap || roomAbove > roomBelow;

      let top = placeAbove
        ? triggerRect.top - tooltipHeight - gap
        : triggerRect.bottom + gap;

      let left = triggerRect.left + (triggerRect.width / 2) - (tooltipWidth / 2);

      if (top < topSafeArea) {
        top = topSafeArea;
      }

      if (top + tooltipHeight > viewportHeight - sidePadding) {
        top = Math.max(topSafeArea, viewportHeight - tooltipHeight - sidePadding);
      }

      if (left < sidePadding) {
        left = sidePadding;
      }

      if (left + tooltipWidth > viewportWidth - sidePadding) {
        left = viewportWidth - tooltipWidth - sidePadding;
      }

      this.tooltipStyle = {
        top: `${Math.round(top)}px`,
        left: `${Math.round(left)}px`,
        maxWidth: `${Math.round(tooltipMaxWidth)}px`,
        visibility: 'visible'
      };
    }
  }
};
</script>

<style scoped>
.glossary-term {
  display: inline-flex;
  align-items: center;
}

.glossary-term__trigger {
  border: 0;
  padding: 0 0.25rem;
  margin: 0;
  background: linear-gradient(180deg, rgba(30, 64, 175, 0.14), rgba(37, 99, 235, 0.18));
  color: #1d4ed8;
  border-radius: 999px;
  cursor: help;
  font: inherit;
  font-weight: 700;
  box-shadow: inset 0 0 0 1px rgba(29, 78, 216, 0.18);
}

.glossary-term__trigger:focus-visible {
  outline: 3px solid rgba(59, 130, 246, 0.28);
  outline-offset: 2px;
}

.glossary-term__tooltip-layer {
  position: fixed;
  z-index: 2200;
  width: max-content;
  display: grid;
  gap: 0.35rem;
  padding: 0.75rem;
  border-radius: 14px;
  background: #0f172a;
  color: #e2e8f0;
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.24);
  pointer-events: none;
  line-height: 1.45;
}

.glossary-term__example {
  color: #93c5fd;
  font-size: 0.9rem;
}

.glossary-fade-enter-active,
.glossary-fade-leave-active {
  transition: opacity 0.12s ease, transform 0.12s ease;
}

.glossary-fade-enter-from,
.glossary-fade-leave-to {
  opacity: 0;
  transform: translateY(4px);
}
</style>